import React from 'react';
import GoogleMapReact from 'google-map-react';
import { Paper, Tooltip, Typography } from '@material-ui/core';
import RoomIcon from '@material-ui/icons/Room';
import StarIcon from '@material-ui/icons/Star';
import mapStyles from '../../mapStyles';

const buildMarkerGroups = (places) => {
  const map = new Map();
  places.forEach((place) => {
    const key = `${Math.round(place.lat * 400) / 400}-${Math.round(place.lng * 400) / 400}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(place);
  });
  return Array.from(map.values()).map((group) => {
    const lat = group.reduce((sum, item) => sum + item.lat, 0) / group.length;
    const lng = group.reduce((sum, item) => sum + item.lng, 0) / group.length;
    return {
      lat,
      lng,
      places: group,
    };
  });
};

const Marker = ({ place, selected, onClick }) => (
  <div>
    <Tooltip title={place.name} enterDelay={250}>
      <Paper
        onClick={onClick}
        style={{
          cursor: 'pointer',
          transform: 'translate(-50%, -50%)',
          borderRadius: 12,
          padding: '8px 10px',
          minWidth: 142,
          maxWidth: 180,
          border: selected ? '2px solid #cf6d4b' : '1px solid rgba(145, 122, 102, 0.35)',
          background: selected ? '#fff5ee' : '#fffdf9',
          boxShadow: selected ? '0 10px 20px rgba(207, 109, 75, 0.2)' : '0 8px 16px rgba(95, 76, 60, 0.16)',
          transition: 'transform 150ms ease, box-shadow 150ms ease',
          transformOrigin: 'center',
          zIndex: selected ? 6 : 4,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <RoomIcon style={{ color: selected ? '#bb5b3a' : '#5f6f57', fontSize: 16 }} />
          <Typography
            variant="subtitle2"
            style={{
              fontWeight: 700,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 130,
            }}
          >
            {place.name}
          </Typography>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <StarIcon style={{ color: '#f59e0b', fontSize: 14 }} />
            <Typography variant="caption" style={{ fontWeight: 700 }}>{place.rating.toFixed(1)}</Typography>
          </div>
          <Typography variant="caption" color="textSecondary">
            {place.priceLevel || 'N/A'}
          </Typography>
        </div>
      </Paper>
    </Tooltip>
  </div>
);

const ClusterMarker = ({ group, onClick }) => (
  <div>
    <Tooltip title={`${group.places.length} places in this area`} enterDelay={250}>
      <Paper
        onClick={onClick}
        style={{
          cursor: 'pointer',
          transform: 'translate(-50%, -50%)',
          borderRadius: 9999,
          padding: '8px 14px',
          border: '1px solid rgba(133, 111, 93, 0.45)',
          background: '#ede2d4',
          boxShadow: '0 8px 16px rgba(95, 76, 60, 0.2)',
          minWidth: 58,
          textAlign: 'center',
        }}
      >
        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#4f3f32', lineHeight: 1.1 }}>
          +{group.places.length} places
        </Typography>
      </Paper>
    </Tooltip>
  </div>
);

const MapPanel = ({
  coords,
  places,
  selectedId,
  onSelect,
  onBoundsChange,
  weatherData,
}) => (
  <div style={{ height: '80vh', minHeight: 620, width: '100%', borderRadius: 18, overflow: 'hidden', position: 'relative', border: '1px solid rgba(169, 144, 124, 0.34)' }}>
    <GoogleMapReact
      bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_MAP_API_KEY, libraries: 'places' }}
      center={coords}
      defaultZoom={13}
      options={{ disableDefaultUI: true, zoomControl: true, styles: mapStyles }}
      onChange={({ center, marginBounds }) => {
        onBoundsChange({
          center: { lat: center.lat, lng: center.lng },
          bounds: {
            ne: marginBounds.ne,
            sw: marginBounds.sw,
          },
        });
      }}
    >
      {buildMarkerGroups(places).map((group, idx) => (
        group.places.length > 1 ? (
          <ClusterMarker
            key={`cluster-${idx}`}
            lat={group.lat}
            lng={group.lng}
            group={group}
            onClick={() => onSelect(group.places[0].id)}
          />
        ) : (
          <Marker
            key={group.places[0].id}
            lat={group.places[0].lat}
            lng={group.places[0].lng}
            place={group.places[0]}
            selected={selectedId === group.places[0].id}
            onClick={() => onSelect(group.places[0].id)}
          />
        )
      ))}
    </GoogleMapReact>
    {weatherData?.list?.[0]?.weather?.[0] && (
      <Paper
        elevation={2}
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          padding: '6px 10px',
          borderRadius: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          zIndex: 2,
          border: '1px solid rgba(169, 144, 124, 0.34)',
          background: '#fff8ef',
        }}
      >
        <img
          src={`https://openweathermap.org/img/w/${weatherData.list[0].weather[0].icon}.png`}
          alt={weatherData.list[0].weather[0].description || 'Forecast'}
          width="24"
          height="24"
          loading="lazy"
        />
        <Typography variant="body2">{weatherData.list[0].weather[0].description}</Typography>
      </Paper>
    )}
  </div>
);

export default MapPanel;
